// Ghost of a recorded run: re-simulates the replay's commands with the same
// deterministic movement code and draws the player model (SessionModel) in a
// translucent "ghost" material, or solid for a watched replay in third person.

import type * as THREE from 'three';
import type { Replay } from '../shared/game/replay';
import { PlayerSession } from '../shared/game/session';
import type { MapLogic } from '../shared/game/mapLogic';
import type { Vec3 } from '../shared/math';
import type { PlayerRenderKit } from './playerModel';
import { SessionModel, type ModelStyle } from './sessionModel';
import type { PhysicsWorld } from '../shared/world';

export class Ghost {
  readonly session: PlayerSession;
  private replay: Replay | null = null;
  private index = 0;
  private prev: Vec3 = [0, 0, 0];
  private prevViewHeight = 40;
  private readonly model: SessionModel;

  /**
   * @param kit the player model (null: a box)
   * @param style translucent ghost, or a solid player (a watched replay)
   */
  constructor(logic: MapLogic, world: PhysicsWorld, kit: PlayerRenderKit | null = null, style: ModelStyle = 'ghost') {
    this.session = new PlayerSession(logic, world);
    this.model = new SessionModel(kit, style);
    this.object.visible = false;
    this.object.renderOrder = 2;
  }

  get object(): THREE.Group {
    return this.model.object;
  }

  /** (Re)starts playback from the replay's first command. */
  start(replay: Replay): void {
    this.replay = replay;
    this.index = 0;
    this.session.health = 100;
    this.session.ps = structuredClone(replay.start);
    this.session.oldButtons = replay.buttons ?? 0;
    this.session.team = replay.team !== undefined && this.session.logic.teams.includes(replay.team) ? replay.team : this.session.logic.defaultTeam;
    // the ghost runs its own movers, from where they were when the run started (with what others did to them)
    this.session.movers.restore(replay.movers);
    this.session.movers.events = [...(replay.moverEvents ?? [])];
    this.session.movers.keepEventsFrom = -Infinity;
    this.prev = [...this.session.ps.origin];
    this.prevViewHeight = this.session.ps.viewheight;
    this.object.visible = true;
    // the animation starts over with the run
    this.model.reset(this.session.anim.eventSeq);
  }

  stop(): void {
    this.replay = null;
    this.object.visible = false;
  }

  get active(): boolean {
    return this.replay !== null;
  }

  /** Time since the start of the replay, in ms. */
  get elapsed(): number {
    return this.replay ? this.index * this.replay.msec : 0;
  }

  /** Commands played so far and in total. */
  get position(): number {
    return this.index;
  }

  get length(): number {
    return this.replay?.cmds.length ?? 0;
  }

  /** Total time of the replay (ms). */
  get duration(): number {
    return this.replay ? this.replay.cmds.length * this.replay.msec : 0;
  }

  /**
   * Jumps to a command of the replay by simulating it again from the start
   * (or on from here when it is ahead): deterministic, so the state is exactly
   * the one of that moment. The skipped moves make no sounds.
   */
  seek(index: number): void {
    const replay = this.replay;
    if (!replay) return;
    index = Math.max(0, Math.min(replay.cmds.length, Math.round(index)));
    if (index < this.index) this.start(replay);
    this.session.muted = true;
    try {
      while (this.index < index) {
        this.session.tick(replay.cmds[this.index++]);
        this.model.tick(this.session.anim, replay.msec);
      }
    } finally {
      this.session.muted = false;
    }
    this.prev = [...this.session.ps.origin];
    this.prevViewHeight = this.session.ps.viewheight;
  }

  /** Command length of the replay being played. */
  get msec(): number {
    return this.replay?.msec ?? 8;
  }

  /** Eye position and view angles at the rendered moment (spectating a replay). */
  view(alpha: number): { origin: Vec3; angles: Vec3 } {
    const ps = this.session.ps;
    const o = ps.origin;
    const p = this.prev;
    const vh = this.prevViewHeight + (ps.viewheight - this.prevViewHeight) * alpha;
    return { origin: [p[0] + (o[0] - p[0]) * alpha, p[1] + (o[1] - p[1]) * alpha, p[2] + (o[2] - p[2]) * alpha + vh], angles: [ps.viewangles[0], ps.viewangles[1], 0] };
  }

  tick(): void {
    if (!this.replay) return;
    this.prev = [...this.session.ps.origin];
    this.prevViewHeight = this.session.ps.viewheight;
    if (this.index >= this.replay.cmds.length) {
      this.stop();
      return;
    }
    this.session.tick(this.replay.cmds[this.index++]);
    // the game side of the animation runs with the moves, like pmove
    this.model.tick(this.session.anim, this.replay.msec);
    // teleports are not interpolated
    const o = this.session.ps.origin;
    if (Math.hypot(o[0] - this.prev[0], o[1] - this.prev[1], o[2] - this.prev[2]) > 64) this.prev = [...o];
  }

  /**
   * @param now client time (ms) for the animation
   * @param viewOrigin camera position (lighting)
   */
  render(alpha: number, now = performance.now(), viewOrigin: ArrayLike<number> = [0, 0, 0]): void {
    if (!this.replay) return;
    const ps = this.session.ps;
    const o = ps.origin;
    const p = this.prev;
    const origin: Vec3 = [p[0] + (o[0] - p[0]) * alpha, p[1] + (o[1] - p[1]) * alpha, p[2] + (o[2] - p[2]) * alpha];
    this.model.render(ps, this.session.anim, origin, ps.viewangles, this.index + 1, now, viewOrigin);
  }

  dispose(): void {
    this.model.dispose();
  }
}
